'use client';

import type { NpoVideoPlayer } from '@npo/player';
import '@npo/player-video-ui/dist/style.css';

export type NpoPlayerSegment = {
    start: number | null;
    end: number | null;
};

export type NpoPlayerCallbacks = {
    onReady: () => void;
    onPlayingChanged: (isPlaying: boolean) => void;
    onEnded: () => void;
};

/**
 * Create an NPO Player in the container and load the stream for the given JWT.
 * The player modules are loaded on demand, because they only work in the browser.
 */
export const createNpoPlayer = async ({
    container,
    jwt,
    segment,
    callbacks,
}: {
    container: HTMLElement;
    jwt: string;
    segment: NpoPlayerSegment;
    callbacks: NpoPlayerCallbacks;
}): Promise<NpoVideoPlayer> => {
    const [
        { NpoVideoPlayer, MediaType, MarkerTypes, TimeBoundActionType },
        { NpoVideoPlayerUIFactory },
    ] = await Promise.all([
        import('@npo/player'),
        import('@npo/player-video-ui'),
    ]);

    // Check if on localhost or (sub)domain contains -test, -acc or -staging
    const isTest = /localhost|test|acc|staging/.test(window.location.hostname);

    const player = new NpoVideoPlayer(
        {
            mediaType: MediaType.VIDEO,
            uiFactory: new NpoVideoPlayerUIFactory(),
            options: { autoplay: false },
            npoTag: {
                initprops: {
                    brand: isTest ? 'Schooltv - dev' : 'Schooltv',
                    brand_id: isTest ? 630385 : 630384,
                    debug: isTest,
                    platform: 'site',
                    platform_version: '1',
                },
            },
        },
        container,
    );

    player.on('error', (event: unknown) => {
        console.error(event);
        callbacks.onReady();
    });
    player.on('canplay', () => callbacks.onReady());
    player.on('play', () => callbacks.onPlayingChanged(true));
    player.on('playing', () => callbacks.onPlayingChanged(true));
    player.on('pause', () => callbacks.onPlayingChanged(false));
    player.on('ended', () => {
        callbacks.onEnded();
        callbacks.onPlayingChanged(false);
    });

    const isSegment = segment.start !== null || segment.end !== null;
    const segmentStart = segment.start ?? 0;

    await player.load(jwt, {
        startOffset: isSegment ? segmentStart : undefined,
        timeBoundActions:
            isSegment && segment.end !== null
                ? [
                      {
                          id: 'fragment',
                          type: TimeBoundActionType.MARKER,
                          markerType: MarkerTypes.SEGMENT,
                          startTime: segmentStart,
                          endTime: segment.end,
                          label: 'Fragment',
                      },
                  ]
                : undefined,
    });

    return player;
};
